import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, Plus, Search, FileText, CheckCircle, Clock, Banknote, Eye } from "lucide-react";
import { format } from "date-fns";

interface GiftAidTabProps {
  ptaId: string;
  onTransactionCreated?: () => void;
}

interface Declaration {
  id: string;
  donor_name: string;
  donor_email: string | null;
  donor_address: string | null;
  donor_postcode: string | null;
  amount: number;
  donation_date: string;
  source: string;
  is_uk_taxpayer: boolean;
  created_at: string;
  claim_id?: string | null;
  claim_status?: string | null;
}

interface Claim {
  id: string;
  claim_number: number;
  status: string;
  total_donations: number;
  claimable_amount: number;
  hmrc_reference: string | null;
  amount_received: number | null;
  submitted_at: string | null;
  received_at: string | null;
  notes: string | null;
  created_at: string;
  item_count?: number;
}

export function GiftAidTab({ ptaId, onTransactionCreated }: GiftAidTabProps) {
  const { toast } = useToast();
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [hmrcSearch, setHmrcSearch] = useState("");

  // Dialogs
  const [createClaimOpen, setCreateClaimOpen] = useState(false);
  const [selectedDeclarationIds, setSelectedDeclarationIds] = useState<string[]>([]);
  const [hmrcRefDialogOpen, setHmrcRefDialogOpen] = useState(false);
  const [receivedDialogOpen, setReceivedDialogOpen] = useState(false);
  const [viewClaimOpen, setViewClaimOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [claimItems, setClaimItems] = useState<Declaration[]>([]);
  const [hmrcRefInput, setHmrcRefInput] = useState("");
  const [receivedAmountInput, setReceivedAmountInput] = useState("");
  const [claimNotes, setClaimNotes] = useState("");

  useEffect(() => {
    if (ptaId) {
      fetchDeclarations();
      fetchClaims();
    }
  }, [ptaId]);

  const fetchDeclarations = async () => {
    setLoading(true);
    // Fetch declarations with their claim status
    const { data: decs, error } = await supabase
      .from("gift_aid_declarations")
      .select("*")
      .eq("pta_id", ptaId)
      .order("donation_date", { ascending: false });

    if (error) {
      console.error("Error fetching declarations:", error);
      setLoading(false);
      return;
    }

    // Fetch claim items to determine which declarations are claimed
    const { data: claimItemsData } = await supabase
      .from("gift_aid_claim_items")
      .select("declaration_id, claim_id, gift_aid_claims(status)")
      .order("created_at");

    const claimMap = new Map<string, { claim_id: string; status: string }>();
    if (claimItemsData) {
      for (const item of claimItemsData) {
        const claimData = item.gift_aid_claims as any;
        claimMap.set(item.declaration_id, {
          claim_id: item.claim_id,
          status: claimData?.status || "unknown",
        });
      }
    }

    const enriched = (decs || []).map((d) => ({
      ...d,
      claim_id: claimMap.get(d.id)?.claim_id || null,
      claim_status: claimMap.get(d.id)?.status || null,
    }));

    setDeclarations(enriched);
    setLoading(false);
  };

  const fetchClaims = async () => {
    const { data, error } = await supabase
      .from("gift_aid_claims")
      .select("*")
      .eq("pta_id", ptaId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching claims:", error);
      return;
    }

    // Get item counts per claim
    const { data: itemCounts } = await supabase
      .from("gift_aid_claim_items")
      .select("claim_id");

    const countMap = new Map<string, number>();
    if (itemCounts) {
      for (const item of itemCounts) {
        countMap.set(item.claim_id, (countMap.get(item.claim_id) || 0) + 1);
      }
    }

    setClaims(
      (data || []).map((c) => ({
        ...c,
        item_count: countMap.get(c.id) || 0,
      }))
    );
  };

  const unclaimedDeclarations = useMemo(
    () => declarations.filter((d) => !d.claim_id),
    [declarations]
  );

  const availableYears = useMemo(() => {
    const years = new Set<string>();
    claims.forEach((c) => {
      const year = new Date(c.created_at).getFullYear().toString();
      years.add(year);
    });
    return Array.from(years).sort((a, b) => b.localeCompare(a));
  }, [claims]);

  const filteredClaims = useMemo(() => {
    let filtered = claims;
    if (yearFilter !== "all") {
      filtered = filtered.filter(
        (c) => new Date(c.created_at).getFullYear().toString() === yearFilter
      );
    }
    if (statusFilter !== "all") {
      filtered = filtered.filter((c) => c.status === statusFilter);
    }
    if (hmrcSearch.trim()) {
      filtered = filtered.filter((c) =>
        c.hmrc_reference?.toLowerCase().includes(hmrcSearch.toLowerCase())
      );
    }
    return filtered;
  }, [claims, yearFilter, statusFilter, hmrcSearch]);

  const toggleDeclarationSelection = (id: string) => {
    setSelectedDeclarationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllUnclaimed = () => {
    if (selectedDeclarationIds.length === unclaimedDeclarations.length) {
      setSelectedDeclarationIds([]);
    } else {
      setSelectedDeclarationIds(unclaimedDeclarations.map((d) => d.id));
    }
  };

  const handleCreateClaim = async () => {
    if (selectedDeclarationIds.length === 0) {
      toast({ title: "No declarations selected", variant: "destructive" });
      return;
    }

    const selectedDecs = declarations.filter((d) =>
      selectedDeclarationIds.includes(d.id)
    );
    const totalDonations = selectedDecs.reduce((sum, d) => sum + d.amount, 0);
    const claimableAmount = totalDonations * 0.25;

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const { data: claim, error: claimError } = await supabase
        .from("gift_aid_claims")
        .insert({
          pta_id: ptaId,
          total_donations: totalDonations,
          claimable_amount: claimableAmount,
          notes: claimNotes || null,
          created_by: user?.id || null,
        })
        .select()
        .single();

      if (claimError) throw claimError;

      const items = selectedDeclarationIds.map((decId) => ({
        claim_id: claim.id,
        declaration_id: decId,
      }));

      const { error: itemsError } = await supabase
        .from("gift_aid_claim_items")
        .insert(items);

      if (itemsError) throw itemsError;

      toast({ title: "Claim created successfully" });
      setCreateClaimOpen(false);
      setSelectedDeclarationIds([]);
      setClaimNotes("");
      fetchDeclarations();
      fetchClaims();
    } catch (error: any) {
      console.error("Error creating claim:", error);
      toast({ title: "Failed to create claim", description: error.message, variant: "destructive" });
    }
  };

  const handleSubmitHmrcRef = async () => {
    if (!selectedClaim || !hmrcRefInput.trim()) return;

    try {
      const { error } = await supabase
        .from("gift_aid_claims")
        .update({
          hmrc_reference: hmrcRefInput.trim(),
          status: "submitted",
          submitted_at: new Date().toISOString(),
        })
        .eq("id", selectedClaim.id);

      if (error) throw error;

      toast({ title: "HMRC reference saved and claim marked as submitted" });
      setHmrcRefDialogOpen(false);
      setHmrcRefInput("");
      setSelectedClaim(null);
      fetchClaims();
    } catch (error: any) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    }
  };

  const handleMarkReceived = async () => {
    if (!selectedClaim) return;

    try {
      const { error } = await supabase
        .from("gift_aid_claims")
        .update({
          status: "received",
          amount_received: receivedAmountInput ? parseFloat(receivedAmountInput) : null,
          received_at: new Date().toISOString(),
        })
        .eq("id", selectedClaim.id);

      if (error) throw error;

      // Record as income in transactions table
      const receivedAmount = receivedAmountInput ? parseFloat(receivedAmountInput) : 0;
      if (receivedAmount > 0) {
        const { error: txError } = await supabase.from("transactions").insert({
          pta_id: ptaId,
          type: "income",
          category: "Gift Aid",
          description: `HMRC Gift Aid claim - Ref: ${selectedClaim.hmrc_reference || 'N/A'}`,
          amount: receivedAmount,
          transaction_date: new Date().toISOString().split("T")[0],
        });
        if (txError) throw txError;
        onTransactionCreated?.();
      }

      toast({ title: "Claim marked as received" });
      setReceivedDialogOpen(false);
      setReceivedAmountInput("");
      setSelectedClaim(null);
      fetchClaims();
    } catch (error: any) {
      toast({ title: "Failed to update", description: error.message, variant: "destructive" });
    }
  };

  const handleViewClaim = async (claim: Claim) => {
    setSelectedClaim(claim);

    const { data: items } = await supabase
      .from("gift_aid_claim_items")
      .select("declaration_id")
      .eq("claim_id", claim.id);

    if (items && items.length > 0) {
      const decIds = items.map((i) => i.declaration_id);
      const { data: decs } = await supabase
        .from("gift_aid_declarations")
        .select("*")
        .in("id", decIds)
        .order("donation_date");

      setClaimItems(decs || []);
    } else {
      setClaimItems([]);
    }

    setViewClaimOpen(true);
  };

  const handleExportCSV = (claim: Claim) => {
    const claimDecs = declarations.filter((d) => d.claim_id === claim.id);

    // HMRC Schedule format
    const headers = [
      "Title", "First Name", "Last Name", "House Name or Number",
      "Postcode", "Aggregated Donations", "Sponsored Event",
      "Donation Date", "Amount"
    ];

    const rows = claimDecs.map((d) => {
      const nameParts = d.donor_name.split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      return [
        "",
        firstName,
        lastName,
        d.donor_address || "",
        d.donor_postcode || "",
        "No",
        "",
        format(new Date(d.donation_date), "dd/MM/yyyy"),
        d.amount.toFixed(2),
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `gift-aid-claim-${claim.claim_number}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "CSV exported" });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "submitted":
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><FileText className="h-3 w-3 mr-1" />Submitted</Badge>;
      case "received":
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="h-3 w-3 mr-1" />Received</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><p className="text-muted-foreground">Loading Gift Aid data...</p></div>;
  }

  if (declarations.length === 0 && claims.length === 0) {
    return (
      <div className="text-center py-12 space-y-4">
        <FileText className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
        <h3 className="text-lg font-semibold">No Gift Aid Declarations Yet</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Gift Aid declarations will appear here when donors opt in during donations, ticket purchases, or credit purchases.
        </p>
      </div>
    );
  }

  const totalUnclaimed = unclaimedDeclarations.reduce((sum, d) => sum + d.amount, 0);
  const totalClaimable = totalUnclaimed * 0.25;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unclaimed Declarations</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unclaimedDeclarations.length}</div>
            <p className="text-xs text-muted-foreground">
              £{totalUnclaimed.toFixed(2)} in donations
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Claimable Amount</CardTitle>
            <Banknote className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">£{totalClaimable.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">25% of unclaimed donations</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Claims</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{claims.length}</div>
            <p className="text-xs text-muted-foreground">
              {claims.filter((c) => c.status === "received").length} received
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="unclaimed" className="space-y-4">
        <TabsList>
          <TabsTrigger value="unclaimed">Unclaimed ({unclaimedDeclarations.length})</TabsTrigger>
          <TabsTrigger value="claims">Claims History ({claims.length})</TabsTrigger>
          <TabsTrigger value="all">All Declarations ({declarations.length})</TabsTrigger>
        </TabsList>

        {/* Unclaimed Declarations */}
        <TabsContent value="unclaimed" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Unclaimed Gift Aid Declarations</CardTitle>
                  <CardDescription>
                    Select declarations to include in a new HMRC claim
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setCreateClaimOpen(true)}
                  disabled={selectedDeclarationIds.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Claim ({selectedDeclarationIds.length})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {unclaimedDeclarations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No unclaimed declarations</p>
                  <p className="text-sm">Gift Aid declarations from donations and ticket purchases will appear here</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedDeclarationIds.length === unclaimedDeclarations.length && unclaimedDeclarations.length > 0}
                          onCheckedChange={selectAllUnclaimed}
                        />
                      </TableHead>
                      <TableHead>Donor</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Claimable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unclaimedDeclarations.map((dec) => (
                      <TableRow key={dec.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedDeclarationIds.includes(dec.id)}
                            onCheckedChange={() => toggleDeclarationSelection(dec.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{dec.donor_name}</div>
                          {dec.donor_email && (
                            <div className="text-xs text-muted-foreground">{dec.donor_email}</div>
                          )}
                        </TableCell>
                        <TableCell>{format(new Date(dec.donation_date), "dd MMM yyyy")}</TableCell>
                        <TableCell>£{dec.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{dec.source}</Badge>
                        </TableCell>
                        <TableCell className="font-medium text-green-700">
                          £{(dec.amount * 0.25).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Claims History */}
        <TabsContent value="claims" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle>Claims History</CardTitle>
                  <CardDescription>Track your HMRC Gift Aid claims</CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search HMRC ref..."
                      className="pl-8 w-48"
                      value={hmrcSearch}
                      onChange={(e) => setHmrcSearch(e.target.value)}
                    />
                  </div>
                  <Select value={yearFilter} onValueChange={setYearFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      {availableYears.map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="received">Received</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredClaims.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No claims found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Claim #</TableHead>
                      <TableHead>Declarations</TableHead>
                      <TableHead>Total Donated</TableHead>
                      <TableHead>Claimable (25%)</TableHead>
                      <TableHead>HMRC Ref</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClaims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell>{format(new Date(claim.created_at), "dd MMM yyyy")}</TableCell>
                        <TableCell className="font-medium">#{claim.claim_number}</TableCell>
                        <TableCell>{claim.item_count}</TableCell>
                        <TableCell>£{claim.total_donations.toFixed(2)}</TableCell>
                        <TableCell className="font-medium">£{claim.claimable_amount.toFixed(2)}</TableCell>
                        <TableCell>{claim.hmrc_reference || "—"}</TableCell>
                        <TableCell>{getStatusBadge(claim.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewClaim(claim)}
                              title="View details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportCSV(claim)}
                              title="Export CSV"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {claim.status === "pending" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setHmrcRefInput(claim.hmrc_reference || "");
                                  setHmrcRefDialogOpen(true);
                                }}
                                title="Enter HMRC Reference"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {claim.status === "submitted" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedClaim(claim);
                                  setReceivedAmountInput(claim.claimable_amount.toFixed(2));
                                  setReceivedDialogOpen(true);
                                }}
                                title="Mark as Received"
                              >
                                <Banknote className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Declarations */}
        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>All Gift Aid Declarations</CardTitle>
              <CardDescription>Complete history of Gift Aid declarations received</CardDescription>
            </CardHeader>
            <CardContent>
              {declarations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No declarations yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Donor</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {declarations.map((dec) => (
                      <TableRow key={dec.id}>
                        <TableCell>
                          <div className="font-medium">{dec.donor_name}</div>
                          {dec.donor_postcode && (
                            <div className="text-xs text-muted-foreground">{dec.donor_postcode}</div>
                          )}
                        </TableCell>
                        <TableCell>{format(new Date(dec.donation_date), "dd MMM yyyy")}</TableCell>
                        <TableCell>£{dec.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{dec.source}</Badge>
                        </TableCell>
                        <TableCell>
                          {dec.claim_id ? (
                            <Badge variant="secondary" className="capitalize">{dec.claim_status}</Badge>
                          ) : (
                            <Badge variant="outline">Unclaimed</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Claim Dialog */}
      <Dialog open={createClaimOpen} onOpenChange={setCreateClaimOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Gift Aid Claim</DialogTitle>
            <DialogDescription>
              This will bundle {selectedDeclarationIds.length} declaration(s) into a new HMRC claim.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span>Declarations:</span>
                <span className="font-medium">{selectedDeclarationIds.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Donated:</span>
                <span className="font-medium">
                  £{declarations
                    .filter((d) => selectedDeclarationIds.includes(d.id))
                    .reduce((sum, d) => sum + d.amount, 0)
                    .toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-green-700 font-bold">
                <span>Claimable (25%):</span>
                <span>
                  £{(declarations
                    .filter((d) => selectedDeclarationIds.includes(d.id))
                    .reduce((sum, d) => sum + d.amount, 0) * 0.25
                  ).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={claimNotes}
                onChange={(e) => setClaimNotes(e.target.value)}
                placeholder="e.g. Summer term donations"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateClaimOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateClaim}>Create Claim</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* HMRC Reference Dialog */}
      <Dialog open={hmrcRefDialogOpen} onOpenChange={setHmrcRefDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enter HMRC Reference</DialogTitle>
            <DialogDescription>
              Enter the reference number from your HMRC submission. This will mark the claim as submitted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>HMRC Reference</Label>
              <Input
                value={hmrcRefInput}
                onChange={(e) => setHmrcRefInput(e.target.value)}
                placeholder="e.g. HMRC-GA-123456"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setHmrcRefDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitHmrcRef} disabled={!hmrcRefInput.trim()}>
                Save & Mark Submitted
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Received Dialog */}
      <Dialog open={receivedDialogOpen} onOpenChange={setReceivedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Claim as Received</DialogTitle>
            <DialogDescription>
              Record the actual amount received from HMRC for claim #{selectedClaim?.claim_number}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted p-3 rounded-lg text-sm">
              <div className="flex justify-between">
                <span>Expected amount:</span>
                <span>£{selectedClaim?.claimable_amount.toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Amount Received (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={receivedAmountInput}
                onChange={(e) => setReceivedAmountInput(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setReceivedDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkReceived}>Mark as Received</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Claim Dialog */}
      <Dialog open={viewClaimOpen} onOpenChange={setViewClaimOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Claim #{selectedClaim?.claim_number}</DialogTitle>
            <DialogDescription>
              {selectedClaim && getStatusBadge(selectedClaim.status)}
              {selectedClaim?.hmrc_reference && (
                <span className="ml-2 text-sm">HMRC Ref: {selectedClaim.hmrc_reference}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Total Donated:</span> <strong>£{selectedClaim?.total_donations.toFixed(2)}</strong></div>
              <div><span className="text-muted-foreground">Claimable:</span> <strong>£{selectedClaim?.claimable_amount.toFixed(2)}</strong></div>
              {selectedClaim?.amount_received !== null && selectedClaim?.amount_received !== undefined && (
                <div><span className="text-muted-foreground">Received:</span> <strong>£{selectedClaim.amount_received.toFixed(2)}</strong></div>
              )}
              {selectedClaim?.notes && (
                <div className="col-span-2"><span className="text-muted-foreground">Notes:</span> {selectedClaim.notes}</div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Donor</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Postcode</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claimItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.donor_name}</TableCell>
                    <TableCell>{format(new Date(item.donation_date), "dd MMM yyyy")}</TableCell>
                    <TableCell>£{item.amount.toFixed(2)}</TableCell>
                    <TableCell>{item.donor_postcode || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
